import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, KeyRound, Tags, Users as UsersIcon, ScrollText, UserCircle, LogOut, Vault } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/credenciais", label: "Credenciais", icon: KeyRound, testId: "nav-credentials" },
  { to: "/cofres", label: "Cofres", icon: Vault, testId: "nav-groups" },
  { to: "/categorias", label: "Categorias", icon: Tags, testId: "nav-categories" },
  { to: "/usuarios", label: "Usuários", icon: UsersIcon, testId: "nav-users", adminOnly: true },
  { to: "/auditoria", label: "Auditoria", icon: ScrollText, testId: "nav-audit", adminOnly: true },
  { to: "/perfil", label: "Perfil", icon: UserCircle, testId: "nav-profile" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-[#F5F5F7]">
      <aside
        className="hidden md:flex md:flex-col w-64 shrink-0 fixed h-full"
        style={{ backgroundColor: "#2F3138" }}
        data-testid="sidebar"
      >
        <div className="px-5 pt-6 pb-4 border-b border-white/5">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Via Permuta" className="w-10 h-10 object-contain bg-white/95 rounded-lg p-1" />
            <div className="leading-tight">
              <div className="text-white font-display font-bold text-base" data-testid="sidebar-brand">Via Permuta</div>
              <div className="text-[10px] tracking-[0.18em] uppercase font-semibold" style={{ color: "#FF8A00" }}>Repositório RP</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.filter((n) => !n.adminOnly || isAdmin).map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testId}
                className={({ isActive }) => `vp-sidebar-link ${isActive ? "active" : ""}`}
              >
                <Icon className="w-[18px] h-[18px]" />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 pb-5 pt-3 border-t border-white/5">
          <div className="px-3 py-2 text-xs text-gray-400 truncate" data-testid="sidebar-user-email">
            {user?.email}
          </div>
          <button
            onClick={onLogout}
            data-testid="sidebar-logout-btn"
            className="w-full vp-sidebar-link"
            style={{ color: "#fca5a5" }}
          >
            <LogOut className="w-[18px] h-[18px]" />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      <div className="w-full min-w-0 md:ml-64 md:w-[calc(100%-16rem)] flex flex-col min-h-screen overflow-x-hidden">
        <header className="bg-white border-b border-gray-200 px-4 sm:px-6 md:px-8 py-4 flex items-center justify-between gap-4 sticky top-0 z-10 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/logo.png" alt="Via Permuta RP" className="w-8 h-8 object-contain md:hidden shrink-0" />
            <h1 className="font-display font-bold text-lg md:text-xl text-[#1F1F24] truncate" data-testid="header-title">
              Via Permuta RP
            </h1>
          </div>
          <div className="flex items-center gap-3 shrink-0" data-testid="header-user">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-[#1F1F24] leading-tight">{user?.name}</div>
              <div className="text-xs text-gray-500 capitalize">{user?.role === "admin" ? "Administrador" : "Membro"}</div>
            </div>
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold shrink-0"
              style={{ backgroundColor: "#FF8A00" }}
              data-testid="header-avatar"
            >
              {(user?.name || "?").slice(0,1).toUpperCase()}
            </div>
          </div>
        </header>

        <nav className="md:hidden bg-[#2F3138] px-2 py-2 flex gap-1 overflow-x-auto">
          {NAV.filter((n) => !n.adminOnly || isAdmin).map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-${n.testId}`}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-md text-[10px] font-semibold whitespace-nowrap ${
                    isActive ? "text-[#FF8A00] bg-white/5" : "text-gray-300"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <main className="flex-1 w-full min-w-0 max-w-full overflow-x-hidden p-4 sm:p-6 md:p-8">{children}</main>
      </div>
    </div>
  );
}
